import { useState, type ReactNode } from 'react'
import type { SessionSubtype } from '@/lib/schemas/db'
import type { BookingRow } from '@/components/BookingCard'
import { BookingsCalendar } from '@/components/BookingsCalendar'
import type { BookingCalendarEvent } from '@/components/BookingsCalendar'
import {
  NextCalloutsList,
  type NextCalloutsMobileItem,
} from '@/screens/NextCalloutsMobileScreen'
import type { CalloutStatus as DbCalloutStatus } from '@/db/domain/calloutStatus'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  NewCalloutScreen,
  type NewCalloutInitialValues,
  type NewCalloutMode,
} from '@/screens/NewCalloutScreen'
import { formatPocketBaseError } from '@/lib/pocketbaseErrors'
import { fromUtcIsoToZaParts, toZaNowDateKey } from '@/lib/datetime'

export interface BookingsScreenProps {
  bookings?: BookingRow[]
  nextCallouts?: NextCalloutsMobileItem[]
  header?: ReactNode
  onNewCalloutClick?: () => void
  onNewSessionClick?: () => void
  onCreateCalloutAtDate?: (date: string) => void
  onCreateSessionAtDate?: (date: string) => void
  onCreateCallout?: (values: NewCalloutInitialValues) => void | Promise<void>
  onUpdateCallout?: (
    bookingId: string,
    values: NewCalloutInitialValues,
  ) => void | Promise<void>
  onMarkCalloutReadyToInvoice?: (bookingId: string) => void
  onCancelCallout?: (bookingId: string) => void
  onSelectCallout?: (id: string) => void
  onLogWork?: (id: string) => void
  onPrepInvoice?: (id: string) => void
  onViewCallout?: (id: string) => void
  onChangeStatus?: (id: string, nextStatus: DbCalloutStatus) => void
  getAuthorDisplayName?: (authorId: string) => string
  /** Optional key scope for persisted UI state (e.g. current user id). */
  storageScope?: string
}

const calloutSaveErrorMessage =
  'Unable to save callout. Please try again.'

const calloutFieldLabels: Record<string, string> = {
  external_id: 'External ID',
  full_name: 'Full name',
  customer_id: 'Customer',
  start_time: 'Start time',
  end_time: 'End time',
  is_whole_day: 'Whole day',
  status: 'Status',
  issue_summary: 'Issue summary',
  contact_address: 'Address',
  contact_name: 'Contact name',
  contact_phone: 'Contact phone',
  contact_email: 'Contact email',
  invoice_name: 'Invoice to',
  invoice_address: 'Invoice address',
  invoice_vat: 'Invoice VAT',
  invoice_email: 'Invoice email',
  invoice_phone: 'Invoice phone',
  invoice_id: 'Invoice ID',
  external_customer_id: 'External customer ID',
}

const getTodayIsoDate = () => toZaNowDateKey()

export function BookingsScreen(props: BookingsScreenProps) {
  const bookings: BookingRow[] = props.bookings ?? []

  const [dialogState, setDialogState] = useState<{
    mode: NewCalloutMode
    booking?: BookingRow
  } | null>(null)
  const [cancelBooking, setCancelBooking] = useState<BookingRow | null>(null)
  const [formError, setFormError] = useState<string | null>(null)
  const [editSessionSubtype, setEditSessionSubtype] = useState<SessionSubtype>('Sit-down Session')
  const editBookingKind =
    dialogState?.booking?.serviceType === 'Session' ? 'Session' : 'Callout'

  const handleNewCalloutClick = () => {
    if (props.onNewCalloutClick) {
      props.onNewCalloutClick()
      return
    }
    setDialogState({ mode: 'create' })
    setFormError(null)
  }

  const internalId = (booking: BookingRow) => booking.privateId ?? booking.id

  const handleSaveNewCallout = async (
    values: NewCalloutInitialValues,
  ) => {
    if (!dialogState) return

    setFormError(null)
    try {
      if (dialogState.mode === 'edit' && dialogState.booking) {
        await props.onUpdateCallout?.(internalId(dialogState.booking), values)
      } else if (dialogState.mode === 'create') {
        await props.onCreateCallout?.(values)
      }

      setDialogState(null)
    } catch (error) {
      setFormError(
        formatPocketBaseError(error, {
          fallbackMessage: calloutSaveErrorMessage,
          fieldLabels: calloutFieldLabels,
        }),
      )
    }
  }

  const handleModifyCallout = (booking: BookingRow) => {
    setDialogState({
      mode: 'edit',
      booking,
    })
    if (booking.serviceType === 'Session') {
      setEditSessionSubtype(booking.sessionSubtype ?? 'TeamViewer Session')
    }
    setFormError(null)
  }

  const handleMarkReadyToInvoice = (booking: BookingRow) => {
    props.onMarkCalloutReadyToInvoice?.(internalId(booking))
  }

  const handleCancelCallout = (booking: BookingRow) => {
    setCancelBooking(booking)
  }

  const handleConfirmCancelCallout = () => {
    if (!cancelBooking) return
    props.onCancelCallout?.(internalId(cancelBooking))
    setCancelBooking(null)
  }

  const newCalloutInitialValues: NewCalloutInitialValues = {
    day: getTodayIsoDate(),
    wholeDay: false,
    customerName: '',
    address: '',
    phone: '',
    email: '',
    invoiceTo: '',
    issueSummary: '',
  }

  const sortedBookings = [...bookings].sort((a, b) => {
    const aStart =
      a.startTime ?? `${a.date}T${a.time ? a.time : '00:00'}`
    const bStart =
      b.startTime ?? `${b.date}T${b.time ? b.time : '00:00'}`
    return aStart.localeCompare(bStart)
  })

  const calendarEvents: BookingCalendarEvent[] = sortedBookings.map(
    (booking) => {
      const isoStart =
        booking.startTime ??
        `${booking.date}T${booking.time ? booking.time : '00:00'}`

      const { day: date, time: timePart } = fromUtcIsoToZaParts(isoStart)

      const startTime = booking.wholeDay ? undefined : timePart

      const endTime = booking.wholeDay
        ? undefined
        : booking.endTime
          ? fromUtcIsoToZaParts(booking.endTime).time
          : undefined

      return {
        id: booking.id,
        date,
        startTime,
        endTime,
        title: `${booking.customerName} – ${booking.issueSummary}`,
        booking,
      }
    },
  )

  const nextCallouts: NextCalloutsMobileItem[] =
    props.nextCallouts ??
    sortedBookings.map((booking) => {
      const startTime =
        booking.startTime ??
        `${booking.date}T${booking.time || '09:00'}:00`
      const endTime = booking.endTime
      const status = (booking.calloutStatus ?? 'Booked') as DbCalloutStatus

      return {
        id: booking.id,
        customerName: booking.customerName,
        customerId: booking.customerId,
        issueSummary: booking.issueSummary,
        status,
        serviceType: booking.serviceType,
        sessionSubtype: booking.sessionSubtype,
        contactAddress: booking.contactAddress ?? '',
        startTime,
        endTime,
        isWholeDay: booking.wholeDay,
      }
    })

  return (
    <div className="bg-slate-50 text-slate-900">
      <main className="w-full max-w-7xl mx-auto px-4 py-6 space-y-4">
        <div className="flex items-center justify-between">
          <h1 className="text-2xl font-semibold">Bookings</h1>
          <div className="flex items-center gap-2">
            <button
              type="button"
              className="hidden sm:inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
              onClick={props.onNewSessionClick}
            >
              New Session
            </button>
            <button
              type="button"
              className="hidden sm:inline-flex items-center rounded-md bg-blue-600 px-3 py-1.5 text-sm font-medium text-white shadow-sm hover:bg-blue-700"
              onClick={handleNewCalloutClick}
            >
              New Callout
            </button>
          </div>
        </div>

        <p className="text-sm text-slate-600">
          Showing active bookings. Use the calendar to visualise availability,
          then jump into the upcoming Callouts below.
        </p>

        <div className="grid gap-4 lg:grid-cols-[minmax(0,1.2fr)_minmax(2fr,0)]">

          <section
            aria-label="Bookings calendar"
            className="order-2 min-h-[320px] md:order-1"
          >
            <BookingsCalendar
              events={calendarEvents}
              getAuthorDisplayName={props.getAuthorDisplayName}
              onModifyCallout={handleModifyCallout}
              onMarkReadyToInvoice={handleMarkReadyToInvoice}
              onCancelCallout={handleCancelCallout}
              onCreateCalloutAtDate={props.onCreateCalloutAtDate}
              onCreateSessionAtDate={props.onCreateSessionAtDate}
            />
          </section>

          <section
            aria-label="Next callouts"
            className="order-1 overflow-visible rounded-2xl border border-slate-200 bg-white md:order-2"
          >
            {nextCallouts.length === 0 ? (
              <div className="px-4 py-6 text-center text-sm text-slate-500">
                No active bookings.
              </div>
            ) : (
              <NextCalloutsList
                callouts={nextCallouts}
                todayDate={getTodayIsoDate()}
                getAuthorDisplayName={props.getAuthorDisplayName}
                onSelectCallout={props.onSelectCallout}
                onLogWork={props.onLogWork}
                onPrepInvoice={props.onPrepInvoice}
                className="bg-white"
                storageScope={props.storageScope}
              />
            )}
          </section>
        </div>

        <Dialog
          open={dialogState !== null}
          onOpenChange={(open) => {
            if (!open) {
              const shouldClose = window.confirm(
                'Discard this draft booking? Unsaved changes will be lost.',
              )
              if (!shouldClose) return

              setDialogState(null)
              setFormError(null)
            }
          }}
        >
          <DialogContent className="sm:max-w-2xl">
            <DialogHeader>
              <DialogTitle>
                {dialogState?.mode === 'edit'
                  ? `Edit ${editBookingKind} booking`
                  : 'New Callout booking'}
              </DialogTitle>
              <DialogDescription>
                {dialogState?.mode === 'edit'
                  ? `Update the selected ${editBookingKind} booking.`
                  : 'Add a new Callout booking.'}
              </DialogDescription>
            </DialogHeader>
            <NewCalloutScreen
              showHeader={false}
              mode={dialogState?.mode}
              errorMessage={formError}
              initialValues={
                dialogState?.booking
                  ? {
                      day: dialogState.booking.date,
                      time: dialogState.booking.wholeDay
                        ? undefined
                        : dialogState.booking.time || undefined,
                      endTime: dialogState.booking.endTime
                        ? fromUtcIsoToZaParts(dialogState.booking.endTime).time
                        : undefined,
                      wholeDay: dialogState.booking.wholeDay ?? false,
                      customerName:
                        dialogState.booking.customerName,
                      address: dialogState.booking.contactAddress ?? '',
                      phone: dialogState.booking.contactPhone ?? '',
                      email: dialogState.booking.contactEmail ?? '',
                      invoiceTo:
                        dialogState.booking.invoiceName ??
                        dialogState.booking.customerName,
                      vat: dialogState.booking.invoiceVat ?? '',
                      invoiceAddress:
                        dialogState.booking.invoiceAddress ??
                        dialogState.booking.contactAddress ?? '',
                      invoiceEmail:
                        dialogState.booking.invoiceEmail ??
                        dialogState.booking.contactEmail ?? '',
                      issueSummary: dialogState.booking.issueSummary,
                      notes: '',
                      sessionSubtype: dialogState.booking.sessionSubtype,
                    }
                  : dialogState?.mode === 'create'
                    ? newCalloutInitialValues
                    : undefined
              }
              bookingExtra={
                dialogState?.mode === 'edit' && dialogState.booking?.serviceType === 'Session' ? (
                  <div>
                    <p className="mb-1 text-xs font-medium text-slate-700">Session type</p>
                    <p className="mb-2 text-[11px] text-slate-500">
                      TeamViewer: “Remote support session” • Sit-down: “In-person at office/customer”
                    </p>
                    <input type="hidden" name="sessionSubtype" value={editSessionSubtype} />
                    <div className="inline-grid w-fit grid-cols-2 rounded-full border border-slate-300 bg-white p-1">
                      <button
                        type="button"
                        className={
                          editSessionSubtype === 'TeamViewer Session'
                            ? 'cursor-pointer rounded-full bg-blue-600 px-4 py-2 text-xs font-semibold text-white'
                            : 'cursor-pointer rounded-full px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100'
                        }
                        onClick={() => setEditSessionSubtype('TeamViewer Session')}
                      >
                        TeamViewer
                      </button>
                      <button
                        type="button"
                        className={
                          editSessionSubtype === 'Sit-down Session'
                            ? 'cursor-pointer rounded-full bg-emerald-600 px-4 py-2 text-xs font-semibold text-white'
                            : 'cursor-pointer rounded-full px-4 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-100'
                        }
                        onClick={() => setEditSessionSubtype('Sit-down Session')}
                      >
                        Sit-down
                      </button>
                    </div>
                  </div>
                ) : undefined
              }
              onSave={handleSaveNewCallout}
              onCancel={() => {
                setDialogState(null)
                setFormError(null)
              }}
            />
          </DialogContent>
        </Dialog>

        <Dialog
          open={cancelBooking !== null}
          onOpenChange={(open) => {
            if (!open) {
              setCancelBooking(null)
            }
          }}
        >
          <DialogContent>
            <DialogTitle>Cancel this Callout?</DialogTitle>
            <DialogDescription>
              This will mark the Callout as cancelled and remove it from the bookings list.
            </DialogDescription>
            <DialogFooter>
              <button
                type="button"
                className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50"
                onClick={() => setCancelBooking(null)}
              >
                Keep Callout
              </button>
              <button
                type="button"
                className="rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700"
                onClick={handleConfirmCancelCallout}
              >
                Cancel Callout
              </button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </main>
    </div>
  )
}

export const demoBookings: BookingRow[] = [
  {
    id: 'b1',
    startTime: '2025-01-10T09:00:00',
    endTime: '2025-01-10T10:00:00',
    wholeDay: false,
    date: '2025-01-10',
    time: '09:00',
    customerName: 'John Doe',
    issueSummary: 'No power to office plugs',
    contactAddress: '123 Main Road, Business Park, Unit 4B',
    serviceType: 'Callout',
    calloutStatus: 'Booked',
    technicianName: 'Alice',
  },
  {
    id: 'b2',
    startTime: '2025-01-10T00:00:00',
    endTime: '2025-01-10T23:59:00',
    wholeDay: true,
    date: '2025-01-10',
    time: '',
    customerName: 'Acme Corp',
    issueSummary:
      'Install additional network points in warehouse and upstairs office area',
    contactAddress: '45 Industrial Road, Warehouse 12',
    serviceType: 'Callout',
    technicianName: 'Bob',
  },
]
